import { useMutation } from "@apollo/client";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import classNames from "classnames";
import { Lightbulb } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { CalloutTip } from "../../../callout-tip";
import { FOCUS_TIME_IDEAL_MINUTES_DEFAULT } from "../../../smart-hold-sync";
import { ButtonGroup, Header, SlideLayout } from "../../shared-components";
import { IconVariant } from "../../shared-components/Header";
import { setError } from "../../utils/setError";
import { FocusTimeSlideRadioGroup } from "./FocusTimeSlideRadioGroup";
import { UpdateFocusTimeSyncAndThresholdNewDocument } from "./__generated__/FocusTimeSlide.generated";
import offUrl from "./assets/focus-off.svg";
import onUrl from "./assets/focus-on.svg";

type FocusTimeSlideProps = {
  showSecondaryButton: boolean;
  onClickPrimaryButton: () => void;
  onClickSecondaryButton: () => void;
  loading: boolean;
  initialFocusTimeSyncEnabled: boolean | undefined;
  initialFocusTimeMinutes: number | undefined;
  orgId: string;
  backFromPreviousSlide: boolean;
};

export const FocusTimeSlide = ({
  showSecondaryButton,
  onClickPrimaryButton,
  onClickSecondaryButton,
  loading,
  initialFocusTimeSyncEnabled,
  initialFocusTimeMinutes,
  orgId,
  backFromPreviousSlide,
}: FocusTimeSlideProps) => {
  const [isFocusTimeEnabled, setIsFocusTimeEnabled] = useState(true);
  const [idealMinutesPerWeek, setIdealMinutesPerWeek] = useState(FOCUS_TIME_IDEAL_MINUTES_DEFAULT);
  const track = useTracking();

  useEffect(() => {
    if (initialFocusTimeSyncEnabled !== undefined) {
      if (!backFromPreviousSlide) {
        // User's first time on this slide, set FT to true
        setIsFocusTimeEnabled(true);
      } else {
        setIsFocusTimeEnabled(initialFocusTimeSyncEnabled);
      }
    }
    if (initialFocusTimeMinutes !== undefined) {
      setIdealMinutesPerWeek(initialFocusTimeMinutes);
    }
  }, [initialFocusTimeMinutes, initialFocusTimeSyncEnabled, backFromPreviousSlide]);

  const [updateSettings, { loading: updating }] = useMutation(
    UpdateFocusTimeSyncAndThresholdNewDocument,
    {
      onError: (error) => {
        setError({
          error: error,
          message: "Failed to update Focus Time settings",
          showUserMessage: true,
        });
      },
    },
  );

  const onSaveAndMoveForward = async () => {
    await updateSettings({
      variables: {
        orgId,
        sync: isFocusTimeEnabled,
        idealMinutesPerWeek,
      },
    });
    onClickPrimaryButton();
  };

  const onChangeFocusTimeSetting = (value: "none" | "some") => {
    setIsFocusTimeEnabled(value === "some");
  };

  const onChangeThreshold = (value: number) => {
    track(TrackingEvents.SETTINGS.UPDATE_SMART_HOLD_IDEAL_MINUTES_PER_WEEK, {
      isOnboarding: true, // This component is only used in onboarding
      idealMinutesPerWeek: value,
      type: "FocusTime",
    });
    setIdealMinutesPerWeek(value);
  };

  let illustrationUrl;
  if (isFocusTimeEnabled) {
    illustrationUrl = onUrl;
  } else {
    illustrationUrl = offUrl;
  }

  return (
    <SlideLayout>
      {/* Having these images preload keeps the fade animation consistent */}
      <img src={onUrl} className="cw-hidden" />
      <img src={offUrl} className="cw-hidden" />

      <Header iconVariant={IconVariant.Thinnerline}>
        Do you need Focus Time to get things done?
      </Header>
      <div className="md:cw-w-[500px] cw-w-full cw-relative">
        <FocusTimeSlideRadioGroup
          selected={isFocusTimeEnabled ? "some" : "none"}
          threshold={idealMinutesPerWeek}
          disabled={loading || updating}
          onChange={onChangeFocusTimeSetting}
          onChangeThreshold={onChangeThreshold}
        />
        <div
          className="cw-h-[265px] cw-mt-8 cw-bg-no-repeat cw-bg-focusTime cw-bg-center cw-transition-[background-image] cw-duration-500"
          style={{ backgroundImage: `url(${illustrationUrl})` }}
        />
        <div
          className={classNames(
            "cw-absolute cw-bottom-3 cw-right-7 cw-w-[200px] md:cw-block cw-hidden cw-transition-opacity cw-duration-500",
            { "cw-opacity-0": !isFocusTimeEnabled, "cw-opacity-1": isFocusTimeEnabled },
          )}
        >
          <CalloutTip tipPosition={"top"} leftAligned={true}>
            <div>
              <Lightbulb color="#fff" />
            </div>
            <div className="cw-text-onEmphasis">
              Focus Time = 2+ hours of uninterrupted time within your working hours.
            </div>
          </CalloutTip>
        </div>
      </div>
      <ButtonGroup
        shouldShowSecondary={showSecondaryButton}
        primaryLabel={"Confirm"}
        onClickPrimary={onSaveAndMoveForward}
        onClickSecondary={onClickSecondaryButton}
        disabled={loading || updating}
      />
    </SlideLayout>
  );
};
